import {
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
  useEffect,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PanelLeftClose,
  PanelLeft,
  Plus,
  Sparkles,
  StopCircle,
  Users,
  Wallet,
  CalendarDays,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ConversationList } from './ConversationList';
import {
  fetchConversations,
  createConversation,
  fetchConversationWithMessages,
  updateConversationTitle,
  archiveConversation,
  type ApiChatMessage,
} from '@/api/chat';
import { ChatFloatingBar } from './ChatFloatingBar';
import { ensurePlainMathCopyHandler } from './plain-math-copy';
import { useStreamingChat } from '@/hooks/useStreamingChat';
import { useToast } from '@/hooks/use-toast';
import type { Conversation, ChatMessage as ChatMessageType } from '../types';

function mapApiConversationToList(c: {
  id: number;
  title: string | null;
  updated_at: string;
  _count: { chat_message: number };
}): Conversation {
  return {
    id: c.id,
    title: c.title ?? 'New conversation',
    lastMessage: '',
    timestamp: new Date(c.updated_at),
    messageCount: c._count.chat_message,
  };
}

function mapApiMessageToMessage(m: ApiChatMessage): ChatMessageType {
  return {
    id: m.id,
    role: m.role as 'user' | 'assistant' | 'system',
    content: m.content ?? '',
    timestamp: new Date(m.created_at),
    responseTime: m.response_time ?? null,
    errorMessage: m.error_message ?? null,
    tokens: m.tokens,
    functionResults: m.chat_function_result?.map((fr) => ({
      resultData: fr.result_data,
      functionCalls: fr.function_calls,
      visualizationType: fr.visualization_type,
      functionName: fr.function_name,
      executionTime: fr.execution_time,
    })),
  };
}

const PROMPT_CATEGORIES: {
  label: string;
  description: string;
  icon: LucideIcon;
  accent: string;
  prompts: string[];
}[] = [
  {
    label: 'Workforce',
    description: 'Headcount, departments, and people',
    icon: Users,
    accent: 'bg-sky-500/10 text-sky-700',
    prompts: [
      'How many employees do we have?',
      'List active employees grouped by department',
      'Who joined in the last 30 days?',
      'Show gender distribution by department',
      'Find employees named John',
      'What is our average tenure?',
    ],
  },
  {
    label: 'Payroll',
    description: 'Costs, payslips, and statutory totals',
    icon: Wallet,
    accent: 'bg-emerald-500/10 text-emerald-700',
    prompts: [
      "Summarise last month's payroll",
      'Break down payroll cost by department',
      'Show statutory contributions for this month',
      'What is the salary distribution?',
      'Show Jean Dupont’s pay history for 6 months',
      'How many employees have active loans?',
    ],
  },
  {
    label: 'Leave & attendance',
    description: 'Balances, overtime, and absences',
    icon: CalendarDays,
    accent: 'bg-amber-500/10 text-amber-700',
    prompts: [
      'Who is on leave today?',
      'Show pending leave requests',
      'Summarise overtime this month',
      'Which employees have low annual leave balances?',
      'Show lateness summary for this month',
      'Attendance summary for yesterday',
    ],
  },
  {
    label: 'Compliance',
    description: 'Warnings, qualifications, and holidays',
    icon: ShieldCheck,
    accent: 'bg-violet-500/10 text-violet-700',
    prompts: [
      'List public holidays this year',
      'Show recent disciplinary warnings',
      'Break down employee qualifications',
      'Upcoming birthdays and work anniversaries',
    ],
  },
];

const USAGE_TIPS = [
  'Ask in English or French',
  'Mention names, departments, or months for sharper results',
];

const DEFAULT_SIDEBAR_WIDTH = 240;
const MIN_SIDEBAR_WIDTH = 160;
const MAX_SIDEBAR_WIDTH = 420;

export function ChatView({
  initialConversationId = null,
}: {
  initialConversationId?: number | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showList, setShowList] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [activeConvId, setActiveConvId] = useState<number | null>(initialConversationId);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);

  useEffect(() => {
    ensurePlainMathCopyHandler();
  }, []);

  useEffect(() => {
    if (initialConversationId != null) {
      setActiveConvId(initialConversationId);
      setShowList(true);
    }
  }, [initialConversationId]);

  const {
    isStreaming,
    streamedContent,
    streamedToolExecutions,
    streamedTotalTokens,
    streamedTotalCost,
    error: streamError,
    streamMessage,
    abort,
    reset,
  } = useStreamingChat();

  const { data: conversationsData = [], isLoading: conversationsLoading } = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => fetchConversations('chat'),
  });

  const conversations: Conversation[] = conversationsData.map(mapApiConversationToList);

  const { data: conversationData, isLoading: messagesLoading } = useQuery({
    queryKey: ['chat', 'conversations', activeConvId],
    queryFn: () => fetchConversationWithMessages(activeConvId!),
    enabled: activeConvId != null,
  });

  const messages: ChatMessageType[] = useMemo(
    () => conversationData?.chat_message.map(mapApiMessageToMessage) ?? [],
    [conversationData]
  );

  const createConvMutation = useMutation({
    mutationFn: async ({ content }: { content: string }) => {
      const title = content.trim().slice(0, 255);
      const conv = await createConversation({ title: title || undefined });
      return { conv, content };
    },
    onSuccess: async ({ conv, content }) => {
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      setActiveConvId(conv.id);
      setPendingUserMessage(content);

      try {
        await streamMessage(conv.id, content);
      } finally {
        setPendingUserMessage(null);
      }
    },
  });

  const updateTitleMutation = useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: number; title: string }) =>
      updateConversationTitle(conversationId, title),
    onSuccess: (_, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', conversationId] });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (conversationId: number) => archiveConversation(conversationId),
    onSuccess: (_, conversationId) => {
      if (activeConvId === conversationId) {
        abort();
        reset();
        setActiveConvId(null);
        setPendingUserMessage(null);
      }
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      queryClient.removeQueries({ queryKey: ['chat', 'conversations', conversationId] });
      toast({ title: 'Conversation deleted', description: 'Removed from your chat history.' });
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Could not delete conversation',
        description: 'Please try again.',
      });
    },
  });

  useLayoutEffect(() => {
    const viewport =
      scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]') ??
      (scrollRef.current?.firstElementChild as HTMLElement | undefined);
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [messages, streamedContent, streamedToolExecutions]);

  const handleNewConversation = useCallback(() => {
    abort();
    reset();
    setActiveConvId(null);
    setPendingUserMessage(null);
  }, [abort, reset]);

  const handleSelectConversation = useCallback(
    (id: number) => {
      abort();
      reset();
      setActiveConvId(id);
      setPendingUserMessage(null);
    },
    [abort, reset]
  );

  const handleDeleteConversation = useCallback(
    (id: number) => {
      archiveMutation.mutate(id);
    },
    [archiveMutation]
  );

  const handleSidebarResizeStart = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      isResizingRef.current = true;
      const startX = event.clientX;
      const startWidth = sidebarWidth;

      const onMove = (moveEvent: MouseEvent) => {
        if (!isResizingRef.current) return;
        const next = Math.min(
          MAX_SIDEBAR_WIDTH,
          Math.max(MIN_SIDEBAR_WIDTH, startWidth + (moveEvent.clientX - startX))
        );
        setSidebarWidth(next);
      };

      const onUp = () => {
        isResizingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [sidebarWidth]
  );

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (activeConvId == null) {
        createConvMutation.mutate({ content });
        return;
      }

      setPendingUserMessage(content);

      try {
        await streamMessage(activeConvId, content);
      } finally {
        setPendingUserMessage(null);
      }
    },
    [activeConvId, createConvMutation, streamMessage]
  );

  const handleStop = useCallback(() => {
    abort();
  }, [abort]);

  const isLoading =
    conversationsLoading ||
    (activeConvId != null && messagesLoading) ||
    createConvMutation.isPending ||
    isStreaming;

  const displayMessages = useMemo(() => {
    const result = [...messages];

    if (
      pendingUserMessage &&
      !messages.some((m) => m.content === pendingUserMessage && m.role === 'user')
    ) {
      result.push({
        id: -1,
        role: 'user',
        content: pendingUserMessage,
        timestamp: new Date(),
        responseTime: null,
        errorMessage: null,
      });
    }

    // Single assistant bubble: show streaming placeholder only while streaming or until refetch delivers the real message
    const lastMessage = result[result.length - 1];
    const lastIsAssistant = lastMessage?.role === 'assistant';
    const showStreamingBubble =
      (streamedContent || isStreaming) && (!lastIsAssistant || isStreaming);

    if (showStreamingBubble) {
      result.push({
        id: -2,
        role: 'assistant',
        content: streamedContent || '',
        timestamp: new Date(),
        responseTime: null,
        errorMessage: streamError,
        toolCalls: streamedToolExecutions,
        tokens: streamedTotalTokens || undefined,
        streamedCost: streamedTotalCost || undefined,
      });
    }

    return result;
  }, [
    messages,
    pendingUserMessage,
    streamedContent,
    isStreaming,
    streamError,
    streamedToolExecutions,
    streamedTotalTokens,
    streamedTotalCost,
  ]);

  return (
    <div className="flex h-full">
      {showList && (
        <div className="relative shrink-0" style={{ width: sidebarWidth }}>
          <ConversationList
            conversations={conversations}
            activeId={activeConvId}
            onSelect={handleSelectConversation}
            onNew={handleNewConversation}
            onDelete={handleDeleteConversation}
            isLoading={conversationsLoading}
            createDisabled={false}
            isDeleting={archiveMutation.isPending}
          />
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat history"
            onMouseDown={handleSidebarResizeStart}
            className="absolute inset-y-0 right-0 z-10 w-1 cursor-col-resize bg-transparent transition-colors hover:bg-border active:bg-primary/40"
          />
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowList(!showList)}
            >
              {showList ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeft className="h-4 w-4" />
              )}
            </Button>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">AI Chat</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isStreaming && (
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={handleStop}>
                <StopCircle className="h-4 w-4" />
                <span className="text-xs">Stop</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-muted-foreground"
              onClick={handleNewConversation}
            >
              <Plus className="h-4 w-4" />
              <span className="text-xs md:text-sm">New conversation</span>
            </Button>
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col">
          {activeConvId == null && !createConvMutation.isPending ? (
            <div className="relative flex min-h-0 flex-1 flex-col">
              <ScrollArea className="min-h-0 flex-1">
                <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 pb-28 pt-8">
                  <div className="space-y-3 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div className="space-y-1.5">
                      <h2 className="text-xl font-semibold tracking-tight">
                        Ask about your payroll data
                      </h2>
                      <p className="mx-auto max-w-lg text-sm text-muted-foreground">
                        Query workforce, payroll, leave, and compliance in plain language. Pick a
                        suggestion below or type your own question.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                      {USAGE_TIPS.map((tip) => (
                        <span
                          key={tip}
                          className="rounded-full border bg-muted/40 px-2.5 py-1 text-xs text-muted-foreground"
                        >
                          {tip}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {PROMPT_CATEGORIES.map((category) => {
                      const Icon = category.icon;
                      return (
                        <section key={category.label} className="space-y-3">
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${category.accent}`}
                            >
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-medium">{category.label}</h3>
                              <p className="text-xs text-muted-foreground">
                                {category.description}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {category.prompts.map((prompt) => (
                              <button
                                key={prompt}
                                type="button"
                                onClick={() => setInputValue(prompt)}
                                disabled={isLoading}
                                className="rounded-xl border bg-background px-3.5 py-2.5 text-left text-sm shadow-sm transition-colors hover:bg-muted/80 disabled:opacity-50"
                              >
                                {prompt}
                              </button>
                            ))}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                </div>
              </ScrollArea>

              <div className="absolute bottom-3 left-0 right-0 z-10 flex justify-center">
                <div className="w-[90%] max-w-3xl">
                  <ChatInput
                    onSend={handleSendMessage}
                    disabled={isLoading}
                    value={inputValue}
                    onValueChange={setInputValue}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              {conversationData && (
                <div className="absolute left-4 right-4 top-2 z-10 flex justify-center md:left-1/2 md:right-auto md:-translate-x-1/2 md:max-w-xl">
                  <ChatFloatingBar
                    title={conversationData.title ?? 'New conversation'}
                    onTitleChange={(newTitle) =>
                      updateTitleMutation.mutate({ conversationId: activeConvId!, title: newTitle })
                    }
                    totalTokens={
                      (conversationData.total_tokens ?? 0) + (isStreaming ? streamedTotalTokens : 0)
                    }
                    totalCost={
                      (conversationData.total_cost ?? 0) + (isStreaming ? streamedTotalCost : 0)
                    }
                    totalResponseTimeMs={conversationData.total_response_time_ms ?? 0}
                  />
                </div>
              )}
              <ScrollArea className="min-h-0 flex-1" ref={scrollRef}>
                <div className="space-y-4 p-4 pb-28 pt-16">
                  {displayMessages.length === 0 && !messagesLoading && (
                    <div className="flex flex-col items-center justify-center py-12 text-center text-sm text-muted-foreground">
                      <p>No messages yet. Send a message to start.</p>
                    </div>
                  )}
                  {displayMessages.map((msg) => (
                    <ChatMessage
                      key={msg.id}
                      message={msg}
                      isStreaming={msg.id === -2 && isStreaming}
                      toolCalls={msg.id === -2 ? streamedToolExecutions : undefined}
                    />
                  ))}
                </div>
              </ScrollArea>

              <div className="absolute bottom-3 left-0 right-0 z-10 flex justify-center">
                <div className="w-[90%] max-w-3xl">
                  <ChatInput
                    onSend={handleSendMessage}
                    disabled={isStreaming}
                    value={inputValue}
                    onValueChange={setInputValue}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
